import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Mail, RefreshCw, User as UserIcon } from 'lucide-react';

const BLANK_FORM = {
  username: '', password: '', role: 'staff',
  first_name: '', last_name: '',
  email: '', employee_code: '', timetec_user_id: '',
  branch: '', branches: [], allowed_roles: [],
  is_active: true, can_work_evenings: false,
  working_days_per_week: 5,
  include_in_gratuities: false,
  hourly_rate: 0,
  stokvel_enabled: false,
  manual_punch_enabled: false,
};

export default function UserEditPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const isNew = userId === 'new' || !userId;

  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(BLANK_FORM);
  const [originalUsername, setOriginalUsername] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const fetchLookups = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/branches`),
        axios.get(`${API}/system-roles`),
      ]);
      setBranches(b.data || []);
      setRoles(r.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchUser = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`);
      const u = (r.data || []).find(x => x.id === userId);
      if (!u) {
        toast.error('User not found');
        navigate('/admin/users');
        return;
      }
      setOriginalUsername(u.username || '');
      setForm({
        username: u.username || '',
        password: '',
        role: u.role,
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email || '',
        employee_code: u.employee_code || '',
        timetec_user_id: u.timetec_user_id || '',
        branch: u.branch || '',
        branches: u.branches || [],
        allowed_roles: u.allowed_roles || [],
        is_active: u.is_active !== false,
        can_work_evenings: !!u.can_work_evenings,
        working_days_per_week: u.working_days_per_week ?? 5,
        include_in_gratuities: u.include_in_gratuities === true,
        hourly_rate: u.hourly_rate ?? 0,
        stokvel_enabled: u.stokvel_enabled === true,
        manual_punch_enabled: u.manual_punch_enabled === true,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load user');
    } finally { setLoading(false); }
  }, [isNew, userId, navigate]);

  useEffect(() => { fetchLookups(); fetchUser(); }, [fetchLookups, fetchUser]);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.username.trim()) { toast.error('Username is required'); return; }
    if (isNew && !form.password.trim()) { toast.error('Password is required for new users'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send blank password on edit
      if (!isNew && !payload.password) delete payload.password;
      if (isNew) {
        await axios.post(`${API}/admin/users`, payload);
        toast.success('User created');
      } else {
        await axios.put(`${API}/admin/users/${userId}`, payload);
        toast.success('User updated');
      }
      navigate('/admin/users');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const removeUser = async () => {
    if (!window.confirm(`Delete user "${originalUsername}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      navigate('/admin/users');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const resendWelcome = async () => {
    if (!form.email) { toast.error('Set an email first'); return; }
    setResending(true);
    try {
      await axios.post(`${API}/account/send-welcome/${userId}`);
      toast.success('Welcome email sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setResending(false); }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return <div className="p-6 text-center text-stone-600">Admins only.</div>;
  }

  const toggleBranch = (name) => {
    const has = form.branches.includes(name);
    update({ branches: has ? form.branches.filter(b => b !== name) : [...form.branches, name] });
  };
  const toggleRole = (code) => {
    const has = form.allowed_roles.includes(code);
    update({ allowed_roles: has ? form.allowed_roles.filter(r => r !== code) : [...form.allowed_roles, code] });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Sticky header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} data-testid="user-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-800 truncate flex items-center gap-2">
                <UserIcon className="w-4 h-4" />
                {isNew ? 'Create user' : `Edit ${originalUsername || 'user'}`}
              </div>
              <div className="text-xs text-stone-500">All settings for this user, one page.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && form.email && (
              <Button variant="outline" size="sm" onClick={resendWelcome} disabled={resending} data-testid="user-resend-welcome-btn">
                <Mail className={`w-3.5 h-3.5 mr-1 ${resending ? 'animate-pulse' : ''}`} />
                Resend welcome
              </Button>
            )}
            {!isNew && (
              <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={removeUser} data-testid="user-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
            <Button size="sm" onClick={save} disabled={saving || loading} data-testid="user-save-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className={`w-3.5 h-3.5 mr-1 ${saving ? 'animate-pulse' : ''}`} />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="max-w-5xl mx-auto p-6 text-center text-stone-500">
          <RefreshCw className="w-5 h-5 inline-block animate-spin mr-2" /> Loading user…
        </div>
      ) : (
        <form onSubmit={save} className="max-w-5xl mx-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Account */}
          <Section title="Account">
            <Row>
              <Field label="First name"><Input value={form.first_name} onChange={e => update({ first_name: e.target.value })} data-testid="user-first-name" /></Field>
              <Field label="Last name"><Input value={form.last_name} onChange={e => update({ last_name: e.target.value })} data-testid="user-last-name" /></Field>
            </Row>
            <Field label="Username *"><Input value={form.username} onChange={e => update({ username: e.target.value })} required data-testid="user-username" /></Field>
            <Field label={isNew ? 'Password *' : 'New password (leave blank to keep current)'}>
              <Input type="password" value={form.password} onChange={e => update({ password: e.target.value })}
                autoComplete="new-password" data-testid="user-password" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={e => update({ email: e.target.value })} data-testid="user-email" />
            </Field>
            <Row>
              <Field label="Role">
                <Select value={form.role} onValueChange={v => update({ role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Active">
                <Toggle checked={form.is_active} onChange={v => update({ is_active: v })} testid="user-active-toggle" />
              </Field>
            </Row>
          </Section>

          {/* Payroll & time */}
          <Section title="Payroll & working time">
            <Field label="Hourly Rate (R / hour)">
              <Input type="number" min="0" step="0.01" value={form.hourly_rate}
                onChange={e => update({ hourly_rate: parseFloat(e.target.value || '0') })}
                placeholder="e.g., 45.50" data-testid="user-hourly-rate" />
              <Hint>Drives indicative pay on staff attendance. Normal x1 · Overtime x1.5 · Sunday x1.5 · PH x2.</Hint>
            </Field>
            <Row>
              <Field label="Working days / week">
                <Input type="number" min="1" max="7" value={form.working_days_per_week}
                  onChange={e => update({ working_days_per_week: parseInt(e.target.value || '5', 10) })}
                  data-testid="user-working-days" />
                <Hint>Used for public-holiday normal hours fallback (5-day → 9h; 6-day → 8h).</Hint>
              </Field>
              <Field label="Can work evenings">
                <Toggle checked={form.can_work_evenings} onChange={v => update({ can_work_evenings: v })} testid="user-evenings-toggle" />
                <Hint>Allow scheduling for shifts ending after 18:00.</Hint>
              </Field>
            </Row>
            <Field label="Include in Gratuities (tip pool)">
              <Toggle checked={form.include_in_gratuities} onChange={v => update({ include_in_gratuities: v })} testid="user-tip-toggle" />
              <Hint>Toggle OFF to exclude this staff member from the Cash Recon tip apportionment.</Hint>
            </Field>
            <Field label="TMC Stokvel access">
              <Toggle checked={form.stokvel_enabled} onChange={v => update({ stokvel_enabled: v })} testid="user-stokvel-toggle" />
              <Hint>When ON, this person sees the TMC Stokvel tab in their staff portal and can apply.</Hint>
            </Field>
            <Field label="Manual punch">
              <Toggle checked={form.manual_punch_enabled} onChange={v => update({ manual_punch_enabled: v })} testid="user-manual-punch-toggle" />
              <Hint>When ON, this person can clock in/out from the Attendance tab in their staff portal (select branch + Clock In/Out). Recorded exactly like a biometric punch.</Hint>
            </Field>
          </Section>

          {/* Branches & roles */}
          <Section title="Branches & shift roles">
            {form.role === 'manager' ? (
              <Field label="Primary branch">
                <Select value={form.branch} onValueChange={v => update({ branch: v })}>
                  <SelectTrigger data-testid="user-primary-branch"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Allowed branches">
                <div className="grid grid-cols-2 gap-1 bg-stone-50 border border-stone-200 rounded p-2 max-h-56 overflow-y-auto">
                  {branches.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-1 py-0.5">
                      <input type="checkbox" checked={form.branches.includes(b.name)} onChange={() => toggleBranch(b.name)} data-testid={`user-branch-${b.name}`} />
                      <span>{b.name}</span>
                    </label>
                  ))}
                  {branches.length === 0 && <div className="text-xs text-stone-400">No branches yet.</div>}
                </div>
              </Field>
            )}
            {form.role === 'staff' && (
              <Field label="Allowed shift roles">
                <div className="grid grid-cols-2 gap-1 bg-stone-50 border border-stone-200 rounded p-2 max-h-56 overflow-y-auto">
                  {roles.map(r => (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-1 py-0.5">
                      <input type="checkbox" checked={form.allowed_roles.includes(r.code)} onChange={() => toggleRole(r.code)} data-testid={`user-shift-role-${r.code}`} />
                      <span><b>{r.code}</b> — {r.role_name}</span>
                    </label>
                  ))}
                  {roles.length === 0 && <div className="text-xs text-stone-400">No shift roles configured.</div>}
                </div>
              </Field>
            )}
          </Section>

          {/* Integrations */}
          <Section title="Integrations & biometric">
            <Field label="Employee code (SimplePay)">
              <Input value={form.employee_code} onChange={e => update({ employee_code: e.target.value })} placeholder="EMP001" data-testid="user-employee-code" />
              <Hint>Used to sync leave from SimplePay.</Hint>
            </Field>
            <Field label="TimeTec / Biometric User ID">
              <Input value={form.timetec_user_id} onChange={e => update({ timetec_user_id: e.target.value })} placeholder="1" data-testid="user-timetec-id" />
              <Hint>Biometric User_ID (differs from employee code). Also used as PIN on ADMS devices.</Hint>
            </Field>
          </Section>

        </form>
      )}
    </div>
  );
}

// ---------- small presentational helpers ----------
const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3">
    <div className="text-sm font-semibold text-stone-700 border-b border-stone-100 pb-2 mb-1">{title}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-stone-600">{label}</Label>
    {children}
  </div>
);
const Hint = ({ children }) => (
  <p className="text-[11px] text-stone-500 leading-snug">{children}</p>
);
const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-stone-300'}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);
